import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { useTenant } from '../../contexts/TenantContext'

const MyPlayers = () => {
  const { tenant } = useTenant()
  const theme = tenant?.settings?.theme || tenant?.theme || 'default'
  const isRedTheme = theme === 'throwball' || tenant?.slug === 'tfi'
  const navigate = useNavigate()
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('') // Filter by member_type
  const [showRequestEditModal, setShowRequestEditModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)

  useEffect(() => {
    fetchPlayers()
  }, [filterStatus])

  const fetchPlayers = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filterStatus) params.status = filterStatus

      const response = await api.get('/user/my-players', { params })
      if (response.data.success) {
        setPlayers(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching players:', error)
      // Axios interceptor handles 401 automatically
      if (error.response?.status !== 401) {
        alert('Failed to fetch players')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (player) => {
    const newStatus = player.status === 'active' ? 'deactivate' : 'activate'
    if (!window.confirm(`Are you sure you want to ${newStatus} ${player.full_name}?`)) return

    try {
      const response = await api.post(`/user/my-players/${player.original_id || player.id}/toggle-status`)
      alert(response.data.message)
      fetchPlayers()
    } catch (error) {
      console.error('Error toggling status:', error)
      alert(error.response?.data?.message || 'Failed to update status')
    }
  }

  const handleRequestEdit = () => {
    // Navigate to request edit page with member details
    if (selectedMember) {
      navigate(`/user/request-edit/${selectedMember.original_id || selectedMember.id}`, {
        state: { member: selectedMember }
      })
    }
  }

  const handleBackClick = () => {
    console.log('Back button clicked!')
    navigate('/user/dashboard')
  }

  const filteredPlayers = useMemo(() => {
    return players.filter(player => {
      const matchesSearch = !searchTerm || (
        player.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.father_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.player_reg_id?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      const playerType = String(player.member_type || 'player').toLowerCase().trim()
      const matchesType = !filterType || playerType === filterType
      return matchesSearch && matchesType
    }).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
  }, [players, filterType, searchTerm])

  return (
    <div className="my-players-page">
      <div className="page-header">
        <div className="header-left">
          <div>
            <h1>My Team Members</h1>
            <p>Manage your registered players, coaches, and managers</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/user/add-player" className="btn-primary">
            <i className="fas fa-plus"></i> Add Team Member
          </Link>
          <button
            className="back-button"
            onClick={handleBackClick}
            type="button"
            style={{ zIndex: 10, pointerEvents: 'auto', position: 'relative' }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="tabs-section">
        <div className="tabs-container">
          <button
            className={`tab-button ${filterType === '' ? 'active' : ''}`}
            onClick={() => setFilterType('')}
          >
            <i className="fas fa-users"></i>
            All Members
            <span className="count-badge">{players.length}</span>
          </button>
          <button
            className={`tab-button ${filterType === 'player' ? 'active' : ''}`}
            onClick={() => setFilterType('player')}
          >
            <i className="fas fa-user"></i>
            Players
            <span className="count-badge">{players.filter(p => (p.member_type || 'player').toLowerCase() === 'player').length}</span>
          </button>
          <button
            className={`tab-button ${filterType === 'coach' ? 'active' : ''}`}
            onClick={() => setFilterType('coach')}
          >
            <i className="fas fa-chalkboard-teacher"></i>
            Coaches
            <span className="count-badge">{players.filter(p => (p.member_type || '').toLowerCase() === 'coach').length}</span>
          </button>
          <button
            className={`tab-button ${filterType === 'manager' ? 'active' : ''}`}
            onClick={() => setFilterType('manager')}
          >
            <i className="fas fa-user-tie"></i>
            Managers
            <span className="count-badge">{players.filter(p => (p.member_type || '').toLowerCase() === 'manager').length}</span>
          </button>
          <button
            className={`tab-button ${filterType === 'official' ? 'active' : ''}`}
            onClick={() => setFilterType('official')}
          >
            <i className="fas fa-id-badge"></i>
            Officials
            <span className="count-badge">{players.filter(p => (p.member_type || '').toLowerCase() === 'official').length}</span>
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name, father name, or registration ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading players...</p>
        </div>
      ) : filteredPlayers.length === 0 ? (
        <div className="no-data">
          <i className="fas fa-inbox"></i>
          <p>No players found</p>
          <Link to="/user/add-player" className="btn-primary">
            <i className="fas fa-plus"></i> Add Your First Player
          </Link>
        </div>
      ) : (
        <div className="table-container">
          <table className="players-table">
            <thead>
              <tr>
                <th>Registration ID</th>
                <th>Type</th>
                <th>Full Name</th>
                <th>Father Name</th>
                <th>Date of Birth</th>
                <th>Age</th>
                <th>Gender</th>
                <th>State</th>
                <th>Position/Role</th>
                <th>Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map(player => {
                const memberType = (player.member_type || 'player').toLowerCase()
                const isOfficial = player.member_type === 'official'
                // Red theme colors for TFI/Throwball
                const typeColors = isRedTheme ? {
                  player: { bg: '#fef2f2', color: '#c00f15', icon: 'fa-user' },
                  coach: { bg: '#fef2f2', color: '#c00f15', icon: 'fa-chalkboard-teacher' },
                  manager: { bg: '#fef2f2', color: '#c00f15', icon: 'fa-clipboard-user' },
                  official: { bg: '#fef2f2', color: '#c00f15', icon: 'fa-id-badge' }
                } : {
                  player: { bg: '#e0f2fe', color: '#0369a1', icon: 'fa-user' },
                  coach: { bg: '#fef3c7', color: '#d97706', icon: 'fa-chalkboard-teacher' },
                  manager: { bg: '#ddd6fe', color: '#7c3aed', icon: 'fa-clipboard-user' },
                  official: { bg: '#dcfce7', color: '#16a34a', icon: 'fa-id-badge' }
                }
                const typeStyle = typeColors[memberType] || typeColors.player

                return (
                <tr key={`${player.source || 'player'}-${player.id}`} className="player-row">
                  <td className="player-reg-id">{player.player_reg_id}</td>
                  <td>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      background: typeStyle.bg,
                      color: typeStyle.color,
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      <i className={`fas ${typeStyle.icon}`}></i>
                      {memberType}
                    </span>
                  </td>
                  <td className="player-name" onClick={() => navigate(`/user/players/${player.original_id || player.id}${isOfficial ? '?source=official' : ''}`)}>
                    {player.full_name}
                  </td>
                  <td>{player.father_name}</td>
                  <td>{player.date_of_birth}</td>
                  <td>{player.age} years</td>
                  <td>{player.gender}</td>
                  <td>{player.state_name || 'N/A'}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '5px 10px',
                      background: typeStyle.bg,
                      color: typeStyle.color,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      border: `1px solid ${typeStyle.color}20`
                    }}>
                      {player.position || '-'}
                    </span>
                  </td>
                  <td>{memberType === 'player' ? (player.category || '-') : 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${(player.status || '').toLowerCase()}`}>
                      {(player.status || '').toLowerCase() === 'inactive' ? 'Blocked' : (player.status || '').toLowerCase() === 'deactivated' ? 'Deactivated' : (player.status || '').charAt(0).toUpperCase() + (player.status || '').slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="actions-cell">
                    {isOfficial ? (
                      <>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/user/players/${player.original_id || player.id}?source=official`)}
                          title="View Official Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <span style={{
                          fontSize: '11px',
                          color: '#16a34a',
                          fontWeight: '500',
                          marginLeft: '8px'
                        }}>
                          <i className="fas fa-shield-alt"></i> Super Admin
                        </span>
                      </>
                    ) : (player.status || '').toLowerCase() === 'inactive' ? (
                      <>
                        <button
                          className="btn-activate"
                          onClick={() => handleToggleStatus(player)}
                          title="Activate Member"
                        >
                          <i className="fas fa-check-circle"></i> Activate
                        </button>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/user/players/${player.original_id || player.id}`)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </>
                    ) : (player.status || '').toLowerCase() === 'deactivated' ? (
                      <>
                        <button
                          className="btn-activate"
                          onClick={() => handleToggleStatus(player)}
                          title="Activate Member"
                        >
                          <i className="fas fa-check-circle"></i> Activate
                        </button>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/user/players/${player.original_id || player.id}`)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        {(player.status || '').toLowerCase() !== 'pending' && (
                          <button
                            className="btn-deactivate-toggle"
                            onClick={() => handleToggleStatus(player)}
                            title="Deactivate Member"
                          >
                            <i className="fas fa-ban"></i> Deactivate
                          </button>
                        )}
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/user/players/${player.original_id || player.id}`)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {memberType === 'player' ? (
                          <button
                            className="btn-request-edit"
                            onClick={() => {
                              setSelectedMember(player)
                              setShowRequestEditModal(true)
                            }}
                            title="Request Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        ) : (
                          <button
                            className="btn-request-edit"
                            onClick={() => navigate(`/user/request-edit/${player.original_id || player.id}`, { state: { member: player, directMode: true } })}
                            title="Edit Member"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Request Edit Information Modal */}
      {showRequestEditModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowRequestEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <i className="fas fa-info-circle info-icon"></i>
              <h2>Request Member Edit</h2>
            </div>
            <div className="modal-body">
              <div className="info-message">
                <i className="fas fa-shield-alt"></i>
                <p>Member profiles are protected and cannot be edited directly. You can submit an edit request to the admin for approval.</p>
              </div>
              <div className="member-info">
                <h4>Member Details:</h4>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">{selectedMember.full_name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Type:</span>
                  <span className="value">{(selectedMember.member_type || 'player').charAt(0).toUpperCase() + (selectedMember.member_type || 'player').slice(1)}</span>
                </div>
                <div className="info-row">
                  <span className="label">Registration ID:</span>
                  <span className="value">{selectedMember.player_reg_id}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setShowRequestEditModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button className="btn-modal-confirm" onClick={handleRequestEdit}>
                <i className="fas fa-paper-plane"></i> Submit Edit Request
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .my-players-page {
          padding: 2rem;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          background: #f9fafb;
          min-height: 100vh;
          box-sizing: border-box;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .header-left {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .back-button {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          transition: all 0.3s;
          position: relative;
          z-index: 10;
          pointer-events: auto;
          color: var(--primary-color, #022a5e);
        }

        .back-button:hover {
          background: rgba(2, 42, 94, 0.05);
          border-color: var(--primary-color, #022a5e);
        }

        .white-theme-layout .back-button {
          color: #ffffff !important;
          background: #c00f15 !important;
          border-color: #c00f15 !important;
          font-weight: 600;
        }

        .white-theme-layout .back-button:hover {
          background: #a00d12 !important;
          border-color: #a00d12 !important;
        }

        .back-button:active {
          transform: scale(0.98);
        }

        .page-header {
          border-top: 4px solid var(--primary-color, #022a5e);
        }

        .white-theme-layout .page-header {
          border-top-color: #c00f15 !important;
          border: 1px solid #fecaca !important;
          border-top: 4px solid #c00f15 !important;
        }

        .page-header h1 {
          color: var(--primary-color, #022a5e);
          margin-bottom: 0.25rem;
        }

        .white-theme-layout .page-header h1 {
          color: #c00f15 !important;
        }

        .page-header p {
          color: #6b7280;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          transition: all 0.3s;
        }

        .white-theme-layout .btn-primary {
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%) !important;
        }

        .white-theme-layout .btn-primary:hover {
          background: linear-gradient(135deg, #a00d12 0%, #8b0a0f 100%) !important;
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.3) !important;
        }

        .tabs-section {
          background: white;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          padding: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .tabs-container {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #f3f4f6;
          border: 2px solid transparent;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.95rem;
          font-weight: 500;
          color: #6b7280;
          transition: all 0.2s;
          position: relative;
        }

        .tab-button:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .tab-button.active {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          color: white;
          border-color: transparent;
        }

        /* TFI/Throwball Red Theme Override */
        .white-theme-layout .tab-button.active {
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%) !important;
        }

        .white-theme-layout .tab-button:hover {
          background: #fef2f2 !important;
          color: #c00f15 !important;
        }

        .tab-button i {
          font-size: 1rem;
        }

        .count-badge {
          background: rgba(255,255,255,0.2);
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-left: 0.25rem;
        }

        .tab-button.active .count-badge {
          background: rgba(255,255,255,0.25);
        }

        .tab-button:not(.active) .count-badge {
          background: #d1d5db;
          color: #374151;
        }

        .btn-primary:hover {
          background: var(--primary-hover, #053e88);
        }

        .filters-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .search-box {
          flex: 1;
          position: relative;
        }

        .search-box i {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .search-box input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: white;
          transition: all 0.2s;
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .white-theme-layout .search-box input:focus {
          border-color: #c00f15 !important;
          box-shadow: 0 0 0 3px rgba(192, 15, 21, 0.1) !important;
        }

        .filter-select {
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: white;
          transition: all 0.2s;
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .white-theme-layout .filter-select:focus {
          border-color: #c00f15 !important;
          box-shadow: 0 0 0 3px rgba(192, 15, 21, 0.1) !important;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow-x: auto;
          max-width: 100%;
        }

        .players-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .players-table thead {
          background: #f9fafb;
        }

        .players-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
        }

        .players-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          color: #6b7280;
        }

        .player-row:hover {
          background: #f9fafb;
        }

        .player-reg-id {
          font-family: monospace;
          color: #6b7280;
          font-weight: 500;
        }

        .player-name {
          color: var(--primary-color, #022a5e);
          font-weight: 500;
          cursor: pointer;
        }

        .player-name:hover {
          text-decoration: underline;
        }

        .white-theme-layout .player-name {
          color: #c00f15 !important;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: capitalize;
          display: inline-block;
        }

        .status-active {
          background: #e5e7eb;
          color: #374151;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
        }

        .status-inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        .actions-cell {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .btn-view {
          padding: 0.5rem 0.75rem;
          background: rgba(2, 42, 94, 0.1);
          color: var(--primary-color, #022a5e);
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
        }

        .btn-view:hover {
          background: rgba(2, 42, 94, 0.2);
        }

        .white-theme-layout .btn-view {
          background: #fef2f2 !important;
          color: #c00f15 !important;
        }

        .white-theme-layout .btn-view:hover {
          background: #fee2e2 !important;
        }

        .btn-edit {
          padding: 0.5rem 0.75rem;
          background: #fef3c7;
          color: #92400e;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
        }

        .btn-edit:hover {
          background: #fde68a;
        }

        .btn-deactivate-toggle {
          padding: 0.5rem 0.875rem;
          background: #fee2e2;
          color: #991b1b;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.3s;
          font-size: 0.8125rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .btn-deactivate-toggle:hover {
          background: #fecaca;
          transform: translateY(-1px);
        }

        .btn-activate {
          padding: 0.5rem 0.875rem;
          background: #d1fae5;
          color: #065f46;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.3s;
          font-size: 0.8125rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .btn-activate:hover {
          background: #a7f3d0;
          transform: translateY(-1px);
        }

        .status-deactivated {
          background: #fef3c7;
          color: #92400e;
        }

        .loading-state, .no-data {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .loading-state i, .no-data i {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: var(--primary-color, #022a5e);
        }

        .white-theme-layout .loading-state i,
        .white-theme-layout .no-data i {
          color: #c00f15 !important;
        }

        .loading-state p, .no-data p {
          color: #6b7280;
          font-size: 1.1rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .my-players-page {
            padding: 1rem;
          }

          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .table-container {
            overflow-x: auto;
          }

          .players-table {
            font-size: 0.75rem;
          }

          .players-table th,
          .players-table td {
            padding: 0.5rem;
          }
        }

        /* Request Edit Button */
        .btn-request-edit {
          padding: 0.5rem 0.875rem;
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;
          font-weight: 500;
        }

        .btn-request-edit:hover {
          background: #d97706;
          transform: translateY(-1px);
        }

        .btn-request-edit i {
          font-size: 0.75rem;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          padding: 2rem 2rem 1.5rem;
          border-bottom: 2px solid #f3f4f6;
          text-align: center;
        }

        .modal-header .info-icon {
          font-size: 3rem;
          color: #3b82f6;
          margin-bottom: 1rem;
        }

        .modal-header h2 {
          margin: 0;
          color: #111827;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-body {
          padding: 2rem;
        }

        .info-message {
          background: #dbeafe;
          border-left: 4px solid #3b82f6;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 0.75rem;
          align-items: start;
        }

        .info-message i {
          color: #1e40af;
          font-size: 1.25rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .info-message p {
          margin: 0;
          color: #1e3a8a;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .member-info h4 {
          color: #374151;
          font-size: 1rem;
          margin: 0 0 1rem 0;
          font-weight: 600;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 0.5rem;
        }

        .info-row:nth-child(even) {
          background: #f3f4f6;
        }

        .info-row .label {
          font-weight: 500;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .info-row .value {
          font-weight: 600;
          color: #111827;
          font-size: 0.9rem;
          text-align: right;
        }

        .modal-actions {
          padding: 1.5rem 2rem 2rem;
          display: flex;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-modal-cancel,
        .btn-modal-confirm {
          flex: 1;
          padding: 0.875rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: none;
        }

        .btn-modal-cancel {
          background: #f3f4f6;
          color: #6b7280;
        }

        .btn-modal-cancel:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-modal-confirm {
          background: #f59e0b;
          color: white;
        }

        .btn-modal-confirm:hover {
          background: #d97706;
        }

        @media (max-width: 640px) {
          .modal-actions {
            flex-direction: column-reverse;
          }

          .btn-modal-cancel,
          .btn-modal-confirm {
            width: 100%;
          }

          .modal-body {
            padding: 1.5rem;
          }

          .info-row {
            flex-direction: column;
            gap: 0.25rem;
          }

          .info-row .value {
            text-align: left;
          }

          .btn-request-edit {
            font-size: 0.75rem;
            padding: 0.4rem 0.7rem;
          }
        }
      `}</style>
    </div>
  )
}

export default MyPlayers
